import { useState } from 'react'
import { usePvpRaids, type PlayerRaidDto } from '@/api/hooks'
import { PvpRaidReport } from '@/components/PvpRaidReport'
import { CombatIcon, ChevronRight } from '@/components/game/GameIcons'

/**
 * Carnet de raids — the captain's PvP history (M10). Both sides of every raid:
 * raids you SENT (you're the attacker) and raids you RECEIVED (you're the
 * defender). Each entry is a {@link PvpRaidReport} framed from the right
 * perspective, with its reused combat replay one click away. Hidden entirely
 * until there's at least one raid (keeps the map clean pre-PvP).
 */
export function RaidLog() {
  const { data } = usePvpRaids()
  const sent = data?.sent ?? []
  const received = data?.received ?? []
  if (sent.length === 0 && received.length === 0) return null

  return (
    <div className="rounded-2xl border border-sand-500/20 bg-ocean-900/40 p-4 sm:p-5 space-y-4">
      <h2 className="font-display text-xl text-sand-300 flex items-center gap-2">
        <CombatIcon className="w-5 h-5 text-rum-400" /> Carnet de raids
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RaidLogColumn label="Tes raids" raids={sent} perspective="attacker" />
        <RaidLogColumn label="Subis" raids={received} perspective="defender" />
      </div>
    </div>
  )
}

// Both PvP logs are unbounded server-side (a full season's raids), so each
// column shows its most recent few and folds the rest behind "voir plus". The
// lists arrive newest-first, so an in-flight raid (the most relevant) stays in
// the visible head.
const RAID_LOG_PREVIEW = 4

// Exported so the collapse can be exercised in isolation under tests.
export function RaidLogColumn({
  label,
  raids,
  perspective,
}: {
  label: string
  raids: PlayerRaidDto[]
  perspective: 'attacker' | 'defender'
}) {
  const [open, setOpen] = useState(false)
  const hidden = raids.length - RAID_LOG_PREVIEW
  const shown = open ? raids : raids.slice(0, RAID_LOG_PREVIEW)

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ocean-300">{label}</p>
      {raids.length === 0 ? (
        <p className="text-xs text-ocean-300 italic">Aucun raid.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((r) => (
            <PvpRaidReport key={r.id} raid={r} perspective={perspective} />
          ))}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-200 transition-colors"
            >
              <ChevronRight className={`w-3.5 h-3.5 text-sand-300 transition-transform ${open ? 'rotate-90' : ''}`} />
              {open ? 'Réduire' : `Voir plus (${hidden})`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
